/**
 * HookHouse v4.0 Suno Integration Service
 *
 * Now uses local backend proxy for:
 * - CORS-free API calls
 * - Automatic token refresh
 * - Centralized auth management
 *
 * Architecture note (AUTO-035 / FEATURE_ENHANCEMENTS.md #7 — Suno API
 * Abstraction Layer): Suno has no official public API as of mid-2026, so
 * every call here goes through ProxySunoClient — a browser-session bearer-
 * token capture + keep-alive workaround. The SunoClient interface below is
 * the seam: when Suno ships an official API, a new class implementing
 * SunoClient can be dropped in via setSunoClient() without touching any
 * call site in the app (all exported functions below are unchanged and
 * simply delegate to whichever client is active).
 */

import { ParsedSunoOutput, SunoClip } from '@/types';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import { isCustomModelMv } from '@/shared/sunoModels';

// Use local proxy in development, direct API in production (with your own proxy)
const getBaseUrl = () => {
  const customProxy = localStorage.getItem('suno_proxy_url');
  if (customProxy) return customProxy;

  // In development, use local backend
  if (import.meta.env.DEV) {
    return '/api/suno';
  }

  // In production, you'd deploy your own proxy
  return localStorage.getItem('suno_proxy_url') || '/api/suno';
};

const getClerkUrl = () => {
  if (import.meta.env.DEV) {
    return '/api/clerk';
  }
  return '/api/clerk';
};

/**
 * Token Management
 * Uses local backend to exchange session cookie for JWT.
 * Proxy-specific — an official-API client would take a plain API key and
 * would not need any of this exchange/cache dance.
 */
let cachedToken: { jwt: string; expiresAt: number } | null = null;

export const renewSunoToken = async (cookie: string): Promise<string> => {
  if (!cookie) throw new Error("Suno Session Cookie is missing.");

  // Check if we have a valid cached token
  if (cachedToken && cachedToken.expiresAt > Date.now()) {
    return cachedToken.jwt;
  }

  try {
    // Try to get cached token from backend first
    const cacheCheck = await fetchWithCsrf(`${getClerkUrl()}/get-cached-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionCookie: cookie })
    });

    const cacheData = await cacheCheck.json();
    if (cacheData.success && cacheData.jwt) {
      cachedToken = {
        jwt: cacheData.jwt,
        expiresAt: Date.now() + (cacheData.expiresIn * 1000)
      };
      return cacheData.jwt;
    }

    // Exchange cookie for new token
    const response = await fetchWithCsrf(`${getClerkUrl()}/exchange-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionCookie: cookie })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `Token exchange failed (${response.status})`);
    }

    const data = await response.json();

    if (!data.jwt) {
      throw new Error("No JWT returned from token exchange");
    }

    // Cache the token locally too
    cachedToken = {
      jwt: data.jwt,
      expiresAt: Date.now() + (data.expiresIn * 1000)
    };

    return data.jwt;

  } catch (err: unknown) {
    console.error("Token Renewal Error:", err);

    // If it looks like a JWT already, just use it
    if (cookie.includes('.') && !cookie.includes(';')) {
      return cookie;
    }

    throw err;
  }
};

/**
 * Get auth headers with valid JWT
 */
const getAuthHeaders = async (tokenOrCookie: string): Promise<Headers> => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('Accept', 'application/json');

  // If it looks like a JWT already, use it directly
  let finalToken = tokenOrCookie;
  if (!tokenOrCookie.includes('.') || tokenOrCookie.includes(';')) {
    try {
      finalToken = await renewSunoToken(tokenOrCookie);
    } catch (e) {
      console.warn("Token renewal failed, attempting raw input as Bearer.");
    }
  }

  headers.append('X-Suno-Authorization', `Bearer ${finalToken.replace('Bearer ', '')}`);
  return headers;
};

/** Per-send settings from the Send to Suno modal. Optional extras are omitted from the request when unset. */
export interface SunoGenerateParams {
  styleStrength?: number;
  weirdness?: number;
  /** Suno's Vocal Gender toggle: "m" / "f"; unset = leave to Suno. */
  vocalGender?: 'm' | 'f' | null;
  /** Suno's Max Mode toggle; only sent when true. */
  maxMode?: boolean;
  /** Suno's Variety slider (aug_creativity): 0 Off, 1 Normal, 2 High, 3 Extra, 4 Max. 0 / unset is not sent. */
  variety?: number;
  /** Suno's Duration > Custom, in seconds (10-360); unset = Auto and is not sent. */
  durationSec?: number | null;
  /** Suno's Personalize (My Taste) toggle; only sent when true. */
  personalize?: boolean;
}

/**
 * SunoClient — the contract this app depends on for every Suno operation.
 * ProxySunoClient (below) is the only implementation today. A future
 * OfficialSunoClient (once Suno ships a real API) implements the same
 * interface and gets activated via setSunoClient() — no call site changes.
 */
export interface SunoClient {
  getCredits(token: string): Promise<any>;
  getFeed(token: string): Promise<SunoClip[]>;
  getClipsByIds(token: string, clipIds: string[]): Promise<SunoClip[]>;
  triggerGeneration(
    data: ParsedSunoOutput,
    token: string,
    model: string,
    params?: SunoGenerateParams
  ): Promise<any>;
  triggerSimpleGeneration(description: string, token: string, model: string, opts: { instrumental: boolean }): Promise<any>;
  triggerVariation(
    data: { prompt: string; tags: string; negativeTags: string; title: string },
    token: string,
    model: string,
    continueClipId: string | null,
    params?: { styleStrength?: number; weirdness?: number }
  ): Promise<any>;
  upsampleTags(token: string, tags: string): Promise<{ tags: string }>;
  getVideoStatus(token: string, clipId: string): Promise<{ status: string; video_url?: string }>;
}

/**
 * ProxySunoClient — today's only implementation. Wraps HookHouse's backend
 * proxy, which itself wraps the unofficial browser-session token workaround.
 * Every method body here is unchanged from the original standalone
 * functions this file used to export directly.
 */
class ProxySunoClient implements SunoClient {
  async getCredits(token: string): Promise<any> {
    const headers = await getAuthHeaders(token);

    const response = await fetchWithCsrf(`${getBaseUrl()}/billing/info`, {
      method: 'GET',
      headers: headers
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error("Suno Auth Error:", response.status, errorBody);
      throw new Error(`Auth Failed (${response.status}). Check Token/Cookie in Settings.`);
    }

    return response.json();
  }

  async triggerGeneration(
    data: ParsedSunoOutput,
    token: string,
    model: string,
    params?: SunoGenerateParams
  ): Promise<any> {
    const headers = await getAuthHeaders(token);

    const rawTags = (data.style && data.style.length > 5) ? data.style : data.styleBlueprint;
    // A blank style is valid for a custom model (Suno then uses the model's default style), so neither field
    // may be assumed to exist.
    const combinedTags = (rawTags || '').substring(0, 1000).trim();
    // Suno's actual excludes cap is 1000 chars. Prior 500-char truncation was
    // silently dropping content the LLM was legitimately producing (500-600 range
    // observed post-Studio-Team run).
    const negativeTags = (data.excludedStyle || "").substring(0, 1000).trim();
    const lyricsPrompt = (data.lyricsWithTags || "").substring(0, 5000);
    const modelSlug = model || "chirp-hawk";

    const style_val = params?.styleStrength ?? data.styleStrength ?? 85;
    const weird_val = params?.weirdness ?? data.weirdness ?? 65;

    const payload: Record<string, unknown> = {
      prompt: lyricsPrompt,
      tags: combinedTags,
      negative_tags: negativeTags,
      title: (data.title || "Untitled").substring(0, 80),
      mv: modelSlug,
      continue_clip_id: null,
      continue_at: null,
      gpt_description_prompt: "",
      make_instrumental: !data.lyricsAlone,
      generation_type: "TEXT",
      metadata: {
        create_mode: "custom",
        control_sliders: {
          style_weight: parseFloat((style_val / 100).toFixed(2)),
          weirdness_constraint: parseFloat((weird_val / 100).toFixed(2))
        }
      }
    };

    // Optional extras, shaped as suno.com's own request carries them (HAR capture 2026-10-11): vocal_gender and
    // is_max_mode sit in metadata, aug_creativity (the Variety slider, whole steps) in control_sliders. Each is
    // left out when unset so a send without them is byte-for-byte what it was before.
    const meta = payload.metadata as { control_sliders: Record<string, number>; [k: string]: unknown };
    if (params?.vocalGender === 'm' || params?.vocalGender === 'f') meta.vocal_gender = params.vocalGender;
    if (params?.maxMode === true) meta.is_max_mode = true;
    // Duration sits at the top level of the body (seen as duration: 285 in the capture), not in metadata.
    if (typeof params?.durationSec === 'number' && params.durationSec >= 10 && params.durationSec <= 360) payload.duration = Math.round(params.durationSec);
    // use_personalization is a top-level field too (captured as true with Personalize On).
    if (params?.personalize === true) payload.use_personalization = true;
    if (typeof params?.variety === 'number' && params.variety > 0) meta.control_sliders.aug_creativity = Math.round(params.variety);

    // suno.com's own custom-mode request has no gpt_description_prompt at all (its request template leaves it
    // unset and only fills it for the description/hybrid flows). Sending it, even empty, can make Suno treat a
    // blank-style request as a description request and write a generic style instead of using the custom
    // model's own default style. Standard-model requests are left exactly as they have always been.
    if (isCustomModelMv(modelSlug)) delete payload.gpt_description_prompt;

    const response = await fetchWithCsrf(`${getBaseUrl()}/generate`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorDetail = 'Failed to trigger Suno generation.';
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || JSON.stringify(errorData);
      } catch (e) {
        errorDetail = `API Error ${response.status}. Check your Token.`;
      }
      throw new Error(errorDetail);
    }

    return response.json();
  }

  /**
   * Simple Mode: one description (up to 3,000 characters), Suno writes the style and the lyrics. The request is the
   * one suno.com sends for Simple Mode, from a HAR capture on 2026-10-20 (AUTO-081): prompt empty, the description in
   * gpt_description_prompt, metadata.create_mode "simple", sent to /api/generate/v2-web/ (the server route picks
   * that path from create_mode). create_session_token and user_tier, which suno.com adds, are left out the same way
   * the Custom Mode request leaves them out.
   */
  async triggerSimpleGeneration(
    description: string,
    token: string,
    model: string,
    opts: { instrumental: boolean }
  ): Promise<any> {
    const headers = await getAuthHeaders(token);
    const payload = {
      token: null,
      task: "agentic_thinking",
      generation_type: "TEXT",
      mv: model,
      prompt: "",
      gpt_description_prompt: description.substring(0, 3000),
      make_instrumental: opts.instrumental,
      user_uploaded_images_b64: null,
      metadata: {
        web_client_pathname: "/create",
        create_surface: "desktop_create_form",
        is_max_mode: false,
        is_mumble: false,
        create_mode: "simple",
        disable_volume_normalization: false,
        control_sliders: { aug_creativity: 0 },
        lyrics_model: "default"
      },
      override_fields: [],
      cover_clip_id: null,
      cover_start_s: null,
      cover_end_s: null,
      persona_id: null,
      artist_clip_id: null,
      artist_start_s: null,
      artist_end_s: null,
      continue_clip_id: null,
      continued_aligned_prompt: null,
      continue_at: null,
      transaction_uuid: crypto.randomUUID(),
      token_provider: null
    };
    const response = await fetchWithCsrf(`${getBaseUrl()}/generate`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      let errorDetail = 'Failed to send the description to Suno.';
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || JSON.stringify(errorData);
      } catch (e) {
        errorDetail = `API Error ${response.status}. Check your Token.`;
      }
      throw new Error(errorDetail);
    }
    return response.json();
  }

  async getFeed(token: string): Promise<SunoClip[]> {
    const headers = await getAuthHeaders(token);

    const response = await fetchWithCsrf(`${getBaseUrl()}/feed?page=0`, {
      method: 'GET',
      headers: headers
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch Suno feed. Token might be expired.`);
    }

    const feedData = await response.json();

    return feedData.map((item: Record<string, unknown>) => ({
      id: item.id as string,
      title: (item.title as string) || 'Untitled',
      imageUrl: item.image_url as string | undefined,
      imageLargeUrl: item.image_large_url as string | undefined,
      audio_url: item.audio_url as string | undefined,
      created_at: item.created_at as string,
      model_name: item.model_name as string,
      metadata: {
        tags: (item.metadata as Record<string, unknown> | undefined)?.tags as string | null || null,
        prompt: (item.metadata as Record<string, unknown> | undefined)?.prompt as string | null || null
      },
      status: item.status as string | undefined,
      is_public: item.is_public as boolean | undefined
    }));
  }

  async triggerVariation(
    data: { prompt: string; tags: string; negativeTags: string; title: string },
    token: string,
    model: string,
    continueClipId: string | null,
    params?: { styleStrength?: number; weirdness?: number }
  ): Promise<any> {
    const headers = await getAuthHeaders(token);

    const style_val = params?.styleStrength ?? 85;
    const weird_val = params?.weirdness ?? 65;

    const payload = {
      prompt: (data.prompt || "").substring(0, 5000),
      tags: (data.tags || "").substring(0, 1000).trim(),
      negative_tags: (data.negativeTags || "").substring(0, 1000).trim(),
      title: (data.title || "Untitled").substring(0, 80),
      mv: model || "chirp-hawk",
      continue_clip_id: continueClipId,
      continue_at: null,
      gpt_description_prompt: "",
      make_instrumental: false,
      generation_type: "TEXT",
      metadata: {
        create_mode: "custom",
        control_sliders: {
          style_weight: parseFloat((style_val / 100).toFixed(2)),
          weirdness_constraint: parseFloat((weird_val / 100).toFixed(2))
        }
      }
    };

    const response = await fetchWithCsrf(`${getBaseUrl()}/generate`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorDetail = 'Failed to trigger Suno variation.';
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || JSON.stringify(errorData);
      } catch (e) {
        errorDetail = `API Error ${response.status}. Check your Token.`;
      }
      throw new Error(errorDetail);
    }

    return response.json();
  }

  async getClipsByIds(token: string, clipIds: string[]): Promise<SunoClip[]> {
    const headers = await getAuthHeaders(token);

    const response = await fetchWithCsrf(`${getBaseUrl()}/feed/v2?ids=${clipIds.join(',')}`, {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch clips by IDs (${response.status})`);
    }

    const data = await response.json();
    const clips = Array.isArray(data) ? data : data.clips || [];

    return clips.map((item: Record<string, unknown>) => ({
      id: item.id as string,
      title: (item.title as string) || 'Untitled',
      imageUrl: item.image_url as string | undefined,
      imageLargeUrl: item.image_large_url as string | undefined,
      audio_url: item.audio_url as string | undefined,
      video_url: item.video_url as string | undefined,
      video_status: item.video_status as string | undefined,
      created_at: item.created_at as string,
      model_name: item.model_name as string,
      metadata: {
        tags: (item.metadata as Record<string, unknown> | undefined)?.tags as string | null || null,
        prompt: (item.metadata as Record<string, unknown> | undefined)?.prompt as string | null || null,
      },
      status: item.status as string | undefined,
      batch_index: item.batch_index as number | undefined,
    }));
  }

  async upsampleTags(token: string, tags: string): Promise<{ tags: string }> {
    const headers = await getAuthHeaders(token);

    const response = await fetchWithCsrf(`${getBaseUrl()}/tags/upsample`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ tags }),
    });

    if (!response.ok) {
      throw new Error(`Tag upsample failed (${response.status})`);
    }

    return response.json();
  }

  async getVideoStatus(token: string, clipId: string): Promise<{ status: string; video_url?: string }> {
    const headers = await getAuthHeaders(token);

    const response = await fetchWithCsrf(`${getBaseUrl()}/video/${clipId}/status`, {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch video status (${response.status})`);
    }

    return response.json();
  }
}

let activeClient: SunoClient = new ProxySunoClient();

/** Swap the active client — the seam a future OfficialSunoClient plugs into. */
export function setSunoClient(client: SunoClient): void {
  activeClient = client;
}

/** Read the currently active client (mainly for tests). */
export function getSunoClient(): SunoClient {
  return activeClient;
}

// ─────────────────────────────────────────────────────────────────────────
// Public API — unchanged signatures, so every existing call site (
// useSunoIntegration.ts, useVariationChains.ts, SunoSyncEngine.tsx,
// SettingsModal.tsx) needs zero changes. Each function simply delegates to
// whichever SunoClient is currently active.
// ─────────────────────────────────────────────────────────────────────────

/** Get Suno credits/billing info */
export const getSunoCredits = (token: string) => activeClient.getCredits(token);

/** Trigger Suno generation */
export const triggerSunoGeneration = (
  data: ParsedSunoOutput,
  token: string,
  model: string,
  params?: SunoGenerateParams
) => activeClient.triggerGeneration(data, token, model, params);

/** Send a Simple Mode description (AUTO-081) */
export const triggerSunoSimpleGeneration = (
  description: string,
  token: string,
  model: string,
  opts: { instrumental: boolean }
) => activeClient.triggerSimpleGeneration(description, token, model, opts);

/** Get Suno feed (generation history) */
export const getSunoFeed = (token: string): Promise<SunoClip[]> => activeClient.getFeed(token);

/** Trigger Suno variation (with continue_clip_id) */
export const triggerVariation = (
  data: { prompt: string; tags: string; negativeTags: string; title: string },
  token: string,
  model: string,
  continueClipId: string | null,
  params?: { styleStrength?: number; weirdness?: number }
) => activeClient.triggerVariation(data, token, model, continueClipId, params);

/** Get specific clips by IDs via targeted v2 endpoint */
export const getSunoClipsByIds = (token: string, clipIds: string[]): Promise<SunoClip[]> =>
  activeClient.getClipsByIds(token, clipIds);

/**
 * Poll specific clips by IDs with cancellation support.
 * Returns a cleanup function to cancel polling. Pure client-side polling
 * orchestration — not part of SunoClient since it's not a single API call.
 */
export const pollClipsByIds = (
  token: string,
  clipIds: string[],
  onUpdate: (clips: SunoClip[]) => void,
  options?: { interval?: number; maxAttempts?: number }
): (() => void) => {
  const interval = options?.interval ?? 4000;
  const maxAttempts = options?.maxAttempts ?? 60;
  let attempts = 0;
  let cancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const poll = async () => {
    if (cancelled || attempts >= maxAttempts) {
      if (!cancelled && attempts >= maxAttempts) {
        onUpdate([]); // Signal timeout with empty array
      }
      return;
    }
    attempts++;

    try {
      const clips = await getSunoClipsByIds(token, clipIds);
      if (cancelled) return;

      onUpdate(clips);

      const allDone = clips.length > 0 && clips.every(
        c => c.status === 'complete' || c.status === 'error'
      );
      if (allDone) return;
    } catch (err) {
      console.error('[pollClipsByIds] Poll error:', err);
    }

    if (!cancelled) {
      timeoutId = setTimeout(poll, interval);
    }
  };

  poll();

  return () => {
    cancelled = true;
    if (timeoutId) clearTimeout(timeoutId);
  };
};

/** Upsample/expand style tags using Suno's AI (Premier feature) */
export const upsampleTags = (token: string, tags: string): Promise<{ tags: string }> =>
  activeClient.upsampleTags(token, tags);

/** Get video generation status for a clip */
export const getVideoStatus = (token: string, clipId: string): Promise<{ status: string; video_url?: string }> =>
  activeClient.getVideoStatus(token, clipId);

/** Clear cached token (useful for logout or token refresh) */
export const clearTokenCache = () => {
  cachedToken = null;
};
